import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { CalendarBlank, Clock, PlayCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Settings() {
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);

    async function load() {
        const { data } = await api.get("/admin/scheduler");
        setCfg(data);
    }
    useEffect(() => {
        load();
    }, []);

    async function save() {
        setSaving(true);
        try {
            await api.put("/admin/scheduler", {
                enabled: cfg.enabled,
                day_of_month: Number(cfg.day_of_month),
                hour: Number(cfg.hour),
            });
            toast.success("Paramètres sauvegardés");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    }

    async function runNow() {
        try {
            await api.post("/admin/scheduler/run-now");
            toast.success("Job exécuté manuellement");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    }

    if (!cfg)
        return (
            <Layout>
                <div className="text-ink-soft">Chargement…</div>
            </Layout>
        );

    return (
        <Layout>
            <div className="mb-8">
                <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-2">Paramètres</p>
                <h1 className="font-display text-4xl font-bold text-ink">Automatisation</h1>
                <p className="text-ink-soft mt-2">
                    Configurez l'envoi automatique du récapitulatif mensuel WhatsApp à tous les clients.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-5xl">
                <div className="bento-card lg:col-span-2 space-y-6">
                    <div className="flex items-center justify-between p-4 rounded-md bg-cream">
                        <div>
                            <p className="font-medium text-ink">Envoi automatique mensuel</p>
                            <p className="text-sm text-ink-soft mt-1">
                                Envoie le récapitulatif WhatsApp du mois écoulé à chaque client.
                            </p>
                        </div>
                        <Switch
                            checked={cfg.enabled}
                            onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
                            data-testid="scheduler-toggle"
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-ink-soft">
                                <CalendarBlank size={14} className="inline mr-1.5" />
                                Jour du mois (1–28)
                            </Label>
                            <Input
                                type="number"
                                min="1"
                                max="28"
                                value={cfg.day_of_month}
                                onChange={(e) => setCfg({ ...cfg, day_of_month: e.target.value })}
                                className="mt-1.5 bg-white"
                                data-testid="scheduler-day-input"
                            />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-widest text-ink-soft">
                                <Clock size={14} className="inline mr-1.5" />
                                Heure (UTC, 0–23)
                            </Label>
                            <Input
                                type="number"
                                min="0"
                                max="23"
                                value={cfg.hour}
                                onChange={(e) => setCfg({ ...cfg, hour: e.target.value })}
                                className="mt-1.5 bg-white"
                                data-testid="scheduler-hour-input"
                            />
                        </div>
                    </div>

                    <div className="flex gap-3 pt-2">
                        <Button
                            onClick={save}
                            disabled={saving}
                            className="bg-terracotta hover:bg-terracotta-dark text-white"
                            data-testid="save-scheduler-button"
                        >
                            {saving ? "Sauvegarde…" : "Sauvegarder"}
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={runNow}
                            className="bg-sand hover:bg-sand/70 text-ink"
                            data-testid="run-now-button"
                        >
                            <PlayCircle size={16} className="mr-1.5" /> Exécuter maintenant
                        </Button>
                    </div>
                </div>

                <div className="bento-card space-y-4">
                    <h3 className="font-display text-lg font-bold text-ink">État du job</h3>
                    <div>
                        <p className="text-xs uppercase tracking-widest text-ink-soft">Statut</p>
                        <Badge className={cfg.enabled ? "bg-sage text-white mt-1.5" : "bg-sand text-ink mt-1.5"}>
                            {cfg.enabled ? "Actif" : "Désactivé"}
                        </Badge>
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-widest text-ink-soft">Prochaine exécution</p>
                        <p className="text-sm text-ink mt-1" data-testid="scheduler-next-run">
                            {cfg.next_run
                                ? new Date(cfg.next_run).toLocaleString("fr-FR")
                                : "—"}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs uppercase tracking-widest text-ink-soft">Dernière exécution</p>
                        {cfg.last_run ? (
                            <div className="text-sm text-ink mt-1">
                                <p>{new Date(cfg.last_run.ran_at).toLocaleString("fr-FR")}</p>
                                <p className="text-xs text-ink-soft mt-0.5">
                                    {cfg.last_run.sent}/{cfg.last_run.total_clients} envois
                                </p>
                            </div>
                        ) : (
                            <p className="text-sm text-ink-soft mt-1">Aucune exécution</p>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
}
